// V3 SIMILAR JOBS ********************************************************************************
// pathname: /v3/jobs/similar
// ************************************************************************************************
import {
  useSwrInfiniteApi,
  ApiJobListCards,
} from '@hooks/useSwrApi';
import { SIMILAR_JOB_LIST_ITEMS_PER_PAGE } from 'src/constants';


// Interface
// ********************************************
interface SimilarJobsInfiniteSwrProps {
  jobSlug?: string;
  shouldFetch?: boolean;
  locale?: string;
  serverSideData?: ApiJobListCards;
  startIndex?: number;
  limit?: number;
  restUrlParams?: string;
}


/**
 * @description SWR: fetch similar jobs list with infinite pagination.
 *
 * * **jobSlug**: string. To generate similar jobs for this job.
 * * **shouldFetch**: boolean. A flag indicating whether to start fetching similar jobs.
 * * **locale**: string. What locale to use for the request.
 * * **serverSideData**: ApiJobListCards, optional. The data we got from the server side request (if we do it). Default undefined.
 * * **startIndex**: number, optional. The first page number to fetch (ie: fetch starting with page 5). Default 1.
 * * **limit**: number, optional. The number of job cards to get per call. Modifies the *limit* url parameter (&limit=24). Default 24.
 * * **restUrlParams**: '&sort=relevant', optional. Default empty string.
 */
export const useSwrSimilarJobsInfinite = (props: SimilarJobsInfiniteSwrProps) => {
  // Props destructuring
  // ********************************************
  const {
    jobSlug,
    shouldFetch = true,
    locale,
    serverSideData,
    startIndex = 0,
    limit = SIMILAR_JOB_LIST_ITEMS_PER_PAGE,
    restUrlParams = '',
  } = props;


  // Get data from API
  // ********************************************
  const {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  } = useSwrInfiniteApi<ApiSchema<'JobResult'>>({
    url: '/v3/jobs/similar',
    shouldFetch,
    locale,
    urlParams: `${jobSlug ? `&jobSlug=${jobSlug}` : ''}${restUrlParams}`,
    serverSideData,
    infiniteConfig: {
      paginationType: 'offset',
      limit,
      // the key where the array of items is located in the response
      responseArrayKey: 'items',
      startIndex,
    },
    apiOptions: {
      keepPreviousData: true,
      revalidateOnFocus: false,
      dedupingInterval: 1000 * 60, // 1 minute
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });


  // Return data
  // ********************************************
  return {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  };
};
